import React, { useState, useEffect, useRef } from "react";
import {
  Building2,
  Home,
  Building,
  Send,
  X,
  Menu,
  MessageSquare,
  Settings,
  LogOut,
  CirclePlus,
  Plus,
  Shield,
  BarChart3,
  Warehouse,
  Trash2,
} from "lucide-react";
import { AppFeature } from "./appFeature";
import ReactMarkdown from "react-markdown";
import { extractImageUrls, isImageUrl } from "../../utils/helpers";
import { Box, Typography } from "@mui/material";
import { sendMessage, sendSessionMessage } from "../../services/api";

type Message = {
  message: string;
  role: string;
  text: string;
  sender: "user" | "ai";
};

type PropertyType = "Apartment" | "Villa" | "Luxury Penthouse";

function Chat({ phoneNumber }: { phoneNumber: string }) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [searchType, setSearchType] = useState(0);
  const sidebarItems = [
    { icon: MessageSquare, label: "Chat", active: true },
    { icon: CirclePlus, label: "Join Waitlist" },
    { icon: Settings, label: "Settings" },
    {
      icon: LogOut,
      label: "Logout",
      action: () => {
        sessionStorage.clear();
        window.location.href = "/";
      },
    },
  ];
  const [loading, setLoading] = useState(false);
  const [newMessage, setNewMessage] = useState("");
  const [messages, setMessages] = useState<Message[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const sessionId = sessionStorage.getItem("sessionId");

  const propertyOptions = [
    {
      type: "Apartment" as PropertyType,
      icon: Building2,
      price: "Starting from $2M",
    },
    {
      type: "Villa" as PropertyType,
      icon: Home,
      price: "Starting from $3.5M",
    },
    {
      type: "Luxury Penthouse" as PropertyType,
      icon: Building,
      price: "Starting from $5M",
    },
    {
      type: "Townhouse" as PropertyType,
      icon: Warehouse,
      price: "Starting from $4M",
    },
  ];
  const searchChips = [
    { label: "I want to buy a property", value: 1 },
    { label: "I'm looking to sell my property", value: 2 },
    { label: "Tell me about rent-to-own", value: 3 },
  ];

  // const handleSendMessage = (e: React.FormEvent) => {
  //   e.preventDefault();
  //   if (!inputMessage.trim()) return;

  //   const newMessages = [
  //     ...messages,
  //     {
  //       message: inputMessage,
  //       role: "user",
  //       text: inputMessage,
  //       sender: "user" as const,
  //     },
  //     {
  //       message:
  //         "I understand you're interested in luxury properties. Could you please provide more details about your preferences? For example, location, number of bedrooms, or any specific amenities you're looking for?",
  //       role: "ai",
  //       text: "I understand you're interested in luxury properties. Could you please provide more details about your preferences? For example, location, number of bedrooms, or any specific amenities you're looking for?",
  //       sender: "ai" as const,
  //     },
  //   ];
  //   setMessages(newMessages);
  //   setInputMessage("");
  // };

  const handleSendMessage = async (e: React.FormEvent) => {
    let localMessage = newMessage;

    e.preventDefault();

    if (!localMessage.trim() || loading) return;

    setMessages([
      ...messages,
      {
        role: "user",
        message: localMessage,
        text: localMessage,
        sender: "user",
      },
    ]);
    setNewMessage("");
    setLoading(true);
    try {
      let response;
      if (phoneNumber == "") {
        console.log("Sending session message");

        response = await sendSessionMessage(localMessage, searchType === 2);
        sessionStorage.setItem(
          "sessionHistory",
          JSON.stringify(response.chatHistory.history)
        );
        setMessages(response.chatHistory.history);
      } else {
        console.log("Sending regular message");
        response = await sendMessage(
          phoneNumber,
          localMessage,
          searchType === 2
        );
        setMessages(response.chatHistory.history);
      }
    } catch (error) {
      console.error("Failed to send message:", error);
    } finally {
      setLoading(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollTo({
      top: messagesEndRef.current.scrollHeight,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (sessionId) {
      const sessionHistory = JSON.parse(
        sessionStorage.getItem("sessionHistory") || "[]"
      );
      sessionHistory && setMessages(sessionHistory);
    }
  }, []);

  return (
    <div
      className="min-h-screen text-black"
      style={{
        background:
          "radial-gradient(circle at center, #feff4b 5%,rgba(255, 253, 230, 0.9) 35%,rgba(255, 255, 255, 0.95) 70%",
      }}
    >
      <button
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        className="md:hidden fixed top-4 left-4 z-50 p-2 rounded-lg bg-gray-800/50 backdrop-blur-sm border border-gray-700"
      >
        {isMobileMenuOpen ? (
          <X className="w-6 h-6 text-blue-400" />
        ) : (
          <Menu className="w-6 h-6 text-blue-400" />
        )}
      </button>

      <div
        className={`group fixed overflow-hidden h-full bg-[#ffffff] backdrop-blur-sm border-r border-gray-300 transition-all duration-300 z-40
          md:w-16 md:hover:w-58 ${isMobileMenuOpen ? "w-48" : "w-0"} md:w-16`}
      >
        <div className="absolute top-[40%] md:top-8 w-full px-4 md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-300">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-400 whitespace-nowrap">
              Chat History
            </h3>
            <button
              // onClick={handleNewChat}
              className="p-1 rounded-md hover:bg-gray-100 text-gray-400 transition-colors"
            >
              <Plus className="w-4 h-4 cursor-pointer" />
            </button>
          </div>

          <div className="space-y-1">
            {/* {chatHistory.map((chat) => (
              <div
                key={chat.id}
                className={`flex items-center justify-between py-2 px-3 rounded-md cursor-pointer group/chat ${
                  chat.active
                    ? "bg-blue-600/20 text-blue-400"
                    : "hover:bg-gray-700/50"
                }`}
                onClick={() => handleChatSelect(chat.id)}
              >
                <span className="text-sm whitespace-nowrap overflow-hidden text-ellipsis pr-2">
                  {chat.title}
                </span>
                <button
                  onClick={(e) => handleDeleteChat(e, chat.id)}
                  className="opacity-0 group-hover/chat:opacity-100 p-1 rounded-md hover:bg-gray-700 text-gray-400 hover:text-red-400 transition-all"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))} */}
          </div>
        </div>
        <nav className="mt-18 md:mt-0 flex flex-col md:justify-end h-full">
          {sidebarItems.map((item, index) => (
            <div
              key={index}
              className={`flex items-center gap-3 px-5 py-3 cursor-pointer transition-colors hover:${
                !item.active && "bg-gray-100"
              } ${item.active ? "bg-[#4285F4] text-white" : ""}`}
              onClick={() => {
                setIsMobileMenuOpen(false);
                item.action && item.action();
              }}
            >
              <item.icon
                className={`${
                  item.icon === LogOut && "rotate-180"
                } w-5 h-5 shrink-0`}
              />
              <span
                className={`whitespace-nowrap tracking-[-0.03rem] ${
                  isMobileMenuOpen ? "opacity-100" : "opacity-0"
                } group-hover:opacity-100 transition-opacity`}
              >
                {item.label}
              </span>
            </div>
          ))}
        </nav>
      </div>

      <div className="mx-auto px-4 py-10 max-w-6xl">
        <h1 className="text-5xl font-bold text-center mb-8 tracking-[-0.03rem]">
          EasyKey.ai
        </h1>
        <h1 className="text-2xl text-center mb-20">Egypt’s First AI Broker</h1>

        {/* Property Options */}
        {/* <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mb-12 px-10">
          {propertyOptions.map((option) => (
            <div
              key={option.type}
              className="bg-gray-800/50 p-3 rounded-xl backdrop-blur-sm border border-gray-700 hover:border-gray-500 cursor-pointer transition-all"
            >
              <div className="flex items-center justify-center text-m sm:text-xl mb-2 sm:mb-3">
                <option.icon className="hidden sm:block w-6 h-8 text-blue-400" />
                <span className="text-blue-400 ml-2 text-center font-medium">
                  {option.type}
                </span>
              </div>
              <h2 className="text-center">{option.price}</h2>
            </div>
          ))}
        </div> */}
        {!messages.length && (
          <div className="flex tracking-[-0.03rem] flex-wrap mb-1 max-w-[90%] sm:max-w-[70%] whitespace-nowrap mx-auto text-gray-700">
            {searchChips.map((chip, i) => (
              <div
                className={`px-2 py-1 m-1 text-sm cursor-pointer border border-[#8c6401] rounded-3xl
                hover:bg-[#4285F4] hover:text-white hover:border-[#4285F4] transition-colors
                ${
                  searchType === chip.value &&
                  "bg-[#4285F4] text-white border-gray-300"
                }`}
                onClick={() => setSearchType(chip.value)}
              >
                {chip.label}
              </div>
            ))}
          </div>
        )}
        {/* Chat Interface */}
        <div
          className={`bg-white max-w-[90%] mx-auto rounded-xl ${
            messages.length ? "border sm:max-w-[60%]" : "sm:max-w-[70%]"
          } border-gray-200 backdrop-blur-sm overflow-hidden`}
        >
          {messages.length > 0 && (
            <div ref={messagesEndRef} className="h-[500px] overflow-y-auto p-6">
              <Box
                sx={{
                  flex: 1,
                  overflowY: "auto",
                  p: 2,
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.5,
                }}
              >
                {messages.map((msg, index) => (
                  <>
                    <span
                      className={`text-sm font-medium ${
                        msg.role === "user"
                          ? "text-blue-400"
                          : "text-emerald-400"
                      } text-${msg.role === "user" ? "right" : "left"}`}
                    >
                      {msg.role === "user" ? "You" : "Talia"}
                    </span>
                    <Box
                      key={index}
                      sx={{
                        maxWidth: "70%",
                        letterSpacing: "-0.03rem",
                        alignSelf:
                          msg.role === "user" ? "flex-end" : "flex-start",
                        backgroundColor:
                          msg.role === "user" ? "#4285F4" : "#F5F5F5",
                        color: msg.role === "user" ? "white" : "#333333",
                        p: 2,
                        borderRadius: 2,
                        "& p": { margin: 0 },
                        "& ul": {
                          marginTop: 1,
                          marginBottom: 1,
                          paddingLeft: 20,
                        },
                        "& li": { marginBottom: 0.5 },
                      }}
                    >
                      {msg.role === "user" ? (
                        <Typography>{msg.message}</Typography>
                      ) : (
                        <>
                          {isImageUrl(msg.message) ? (
                            <>
                              <Typography sx={{ mb: 1 }}>
                                {
                                  msg.message.split(
                                    /https?:\/\/[^\s]+\.(?:jpg|jpeg|gif|png)/gi
                                  )[0]
                                }
                              </Typography>
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: 1,
                                }}
                              >
                                {extractImageUrls(msg.message).map(
                                  (url, index) => (
                                    <img
                                      key={index}
                                      src={url}
                                      alt={`Response ${index + 1}`}
                                      style={{
                                        maxWidth: "100%",
                                        height: "auto",
                                        borderRadius: "8px",
                                      }}
                                      onError={(e) => {
                                        (e.target as HTMLImageElement).onerror =
                                          null;
                                        (e.target as HTMLImageElement).src =
                                          "fallback-image.png";
                                      }}
                                    />
                                  )
                                )}
                              </Box>
                            </>
                          ) : (
                            <ReactMarkdown>
                              {msg.message
                                ? msg.message.replace(/\*\*/g, "")
                                : ""}
                            </ReactMarkdown>
                          )}
                        </>
                      )}
                    </Box>
                    {loading && index === messages.length - 1 && (
                      <div className="mb-6 text-left">
                        <div className="mb-1">
                          <span className="text-sm font-medium text-emerald-400">
                            Talia
                          </span>
                        </div>
                        <div className="inline-block p-4 rounded-xl max-w-[80%] bg-[#F5F5F5] text-[#333333] rounded-tl-none">
                          <div className="flex items-center">
                            <span className="mr-2 flex tracking-[-0.03rem] text-[#333333]">
                              Thinking
                              <span className="animate-pulse ml-1">.</span>
                              <span className="animate-pulse animation-delay-200">
                                .
                              </span>
                              <span className="animate-pulse animation-delay-400">
                                .
                              </span>
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                ))}
              </Box>
            </div>
          )}

          {/* Input Area */}
          <form
            onSubmit={handleSendMessage}
            className={`p-4 bg-[#ffffff] ${
              messages.length && "border-t"
            } border-gray-200`}
          >
            <div className="flex gap-4">
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder={
                  messages.length
                    ? "Message Talia AI..."
                    : "Describe what you're looking for and your budget..."
                }
                className={`flex-1 bg-[#F5F5F5] tracking-[-0.05rem] text-black rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-gray-100`}
              />
              <button
                type="submit"
                disabled={searchType === 0}
                className="bg-black tracking-[-0.03rem] hover:bg-gray-700 disabled:bg-gray-400 disabled:cursor-not-allowed cursor-pointer text-white px-6 py-2 rounded-lg flex items-center gap-2 transition-colors"
              >
                <Send className="w-4 h-4" />
                {messages.length ? "Send" : "Start Conversation"}
              </button>
            </div>
          </form>
        </div>
        {/* {messages.length && <AppFeature />} */}
      </div>
    </div>
  );
}

export default Chat;
